"use client"

import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "sonner"
import { QuestionResponse, adminQuestionsService } from "@/services/admin-questions"
import { SAT_DOMAINS } from "@/lib/constants"
import { Loader2 } from "lucide-react"

interface MetadataEditorModalProps {
    isOpen: boolean;
    onClose: () => void;
    question: QuestionResponse | null;
    onUpdate?: (updatedQuestion: QuestionResponse) => void;
}

export function MetadataEditorModal({ isOpen, onClose, question, onUpdate }: MetadataEditorModalProps) {
    const [saving, setSaving] = useState(false)
    const [providers, setProviders] = useState<{ code: string, name: string }[]>([])

    // Form State
    const [providerCode, setProviderCode] = useState("")
    const [domain, setDomain] = useState("")
    const [skill, setSkill] = useState("")
    const [mcqAnswer, setMcqAnswer] = useState("")
    const [sprAnswersInput, setSprAnswersInput] = useState("")
    const [status, setStatus] = useState("")

    // Load Providers
    useEffect(() => {
        if (isOpen) {
            adminQuestionsService.getProviders().then(setProviders).catch(console.error)
        }
    }, [isOpen])

    // Load Question Data
    useEffect(() => {
        if (question && isOpen) {
            setProviderCode(question.providerCode || "PP")
            setDomain(question.domainName || "")
            setSkill(question.skillName || "")
            setMcqAnswer(question.mcqAnswer || "")
            setSprAnswersInput(question.sprAnswers ? question.sprAnswers.join(", ") : "")
            setStatus(question.status || "active")
        }
    }, [question, isOpen])

    const handleSave = async () => {
        if (!question) return
        setSaving(true)
        try {
            const updates: any = {
                provider_code: providerCode,
                provider_name: providers.find(p => p.code === providerCode)?.name || "Practice Portal",
                domain_name: domain,
                skill_name: skill,
                status: status,
            }

            if (question.questionType === 'mcq') {
                updates.mcq_answer = mcqAnswer
                // Should we update correct_answer alias? Backend usually derives it or has mcq_answer column
                updates.correct_answer = mcqAnswer
            } else {
                const answers = sprAnswersInput.split(",").map(s => s.trim()).filter(Boolean)
                updates.spr_answers = answers
                updates.correct_answer = answers[0] || "" // fallback
            }

            const updated = await adminQuestionsService.updateQuestion(question.questionId || String(question.id), updates)
            toast.success("Metadata updated successfully")
            onUpdate?.(updated) // Notify parent
            onClose()
        } catch (error) {
            console.error(error)
            toast.error("Failed to update metadata")
        } finally {
            setSaving(false)
        }
    }

    if (!question) return null

    const skills = domain ? SAT_DOMAINS[domain as keyof typeof SAT_DOMAINS] || [] : []

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Edit Metadata</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    {/* Provider */}
                    <div className="grid gap-2">
                        <Label>Provider</Label>
                        <Select value={providerCode} onValueChange={setProviderCode}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Provider" />
                            </SelectTrigger>
                            <SelectContent>
                                {providers.map(p => (
                                    <SelectItem key={p.code} value={p.code}>{p.name} ({p.code})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Domain */}
                    <div className="grid gap-2">
                        <Label>Domain</Label>
                        <Select value={domain} onValueChange={(val) => {
                            setDomain(val)
                            setSkill("") // Reset skill
                        }}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Domain" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.keys(SAT_DOMAINS).map(d => (
                                    <SelectItem key={d} value={d}>{d}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Skill */}
                    <div className="grid gap-2">
                        <Label>Skill</Label>
                        <Select value={skill} onValueChange={setSkill} disabled={!domain}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Skill" />
                            </SelectTrigger>
                            <SelectContent>
                                {skills.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Correct Answer */}
                    <div className="grid gap-2">
                        <Label>Correct Answer</Label>
                        {question.questionType === 'mcq' ? (
                            <Select value={mcqAnswer} onValueChange={setMcqAnswer}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Answer" />
                                </SelectTrigger>
                                <SelectContent>
                                    {['A', 'B', 'C', 'D'].map(opt => (
                                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                value={sprAnswersInput}
                                onChange={(e) => setSprAnswersInput(e.target.value)}
                                placeholder="e.g. 5, 10/2 (comma separated)"
                            />
                        )}
                    </div>

                    {/* Status */}
                    <div className="grid gap-2">
                        <Label>Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="deactivated">Deactivated</SelectItem>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
